import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import Select, { IntegerDropdown, MultiSelect } from "shared/components/select/Select";
import { Checkbox } from "shared/components/Checkbox";
import ReactDatePicker from "react-datepicker";
import { connect } from "react-redux";
import { Uploader } from "../../../shared/components/Uploader";
import { updateParameter } from "../../actions/UpdateParameter";
import {
    CycleMonthDays, CycleMonthRanks, CycleMonths, CycleMonthType,
    CycleType, ExtendedWeekDays, ICheckBoxViewModel, IScheduleParameters, IValueLabel,
    RepeatDelayType, WeekDays
} from "../../model/ITriggerData";

import { State } from "../../model/State";
import { default as DropdownInput } from "../DropdownInput";
import { default as TriggerInput, InputMode } from "./TriggerInput";
import { DispatchType, Utilities } from "../../../shared/utilities";
import { getTimeZoneSelectModel } from "./TimeZoneSelectModelSelector";
import { Option } from "shared/model/Option";
import { SwitchCollapse } from "../SwitchCollapse";
import Time from "shared/Time";

enum ChosenDateType {
    Never = "Never",
    Only = "Only"
}

const popperOverflowModifier  = [
    {
        name: "preventOverflow",
        options: {
            rootBoundary: "viewport",
            tether: false,
            altAxis: true,
        }
    }
];

export class ScheduleTrigger extends React.Component<ScheduleTriggerProps, ScheduleTriggerState> {
    constructor(props: ScheduleTriggerProps) {
        super(props);

        const value = _.get(this.props.existingParameters, "seasonEndDate", null);
        const isValueSet = value == null ? false : true;
        const cycleTypeValue = _.get(this.props.existingParameters, "cycleType", null);
        const cycleRecurEveryValue = _.get(this.props.existingParameters, "cycleRecurEvery", null);
        const displayDetails = isValueSet || cycleTypeValue !== "Daily" || cycleRecurEveryValue !== 1 ? true : false;

        this.state = {
            showDetails: displayDetails,
            showSeasonEndDate: isValueSet,
            selectedChosenDates: [],
            chosenDatesType: props.existingParameters?.openOnlyOnDates
                ? ChosenDateType.Only
                : ChosenDateType.Never
        };
    }

    private getCheckBoxViewModels(key: string, possibleValues: IValueLabel[]): ICheckBoxViewModel[] {
        const list: string = _.get(this.props.existingParameters, key, "None");
        const items = (!list || list === "None") ? [] : list.split(",");
        return possibleValues.map((v) => {
            return {
                label: v.label,
                value: v.value,
                selected: items.indexOf(v.value) !== -1
            };
        });
    }

    private isRecurringDisabled(): boolean {
        return !_.get(this.props.existingParameters, "recurring", false);
    }

    private handleChange = (selectedDate: Date | null, key: keyof (IScheduleParameters)) => {
        if (this.props.onChange && selectedDate) {
            this.props.onChange(Time.format(selectedDate, Time.dateFormat), key);
        }
    }

    private handleCheckBoxClick = (value: string, key: keyof (IScheduleParameters), isSelected: boolean) => {
        const currentSelection = _.get(this.props.existingParameters, key, "") as string;
        const items = (!currentSelection || currentSelection === "None") ? [] : currentSelection.split(",");
        if (isSelected) {
            // Remove selection
            items.splice(items.indexOf(value), 1);
            if (this.props.onChange) {
                this.props.onChange(items.join(), key);
            }
        } else {
            // add selection
            items.push(value);
            if (this.props.onChange) {
                this.props.onChange(items.join(), key);
            }
        }
    }

    private getDate(key: string): Date {
        const value = _.get(this.props.existingParameters, key);
        return value == null ? new Date() : new Date(value);
    }

    private handleChosenDateTypeChanged = (e: Option) => {
        this.clearChosenDates(); // always clear so we do not have any overlap
        this.setState({ chosenDatesType: (ChosenDateType as { [key: string]: ChosenDateType })[e.value] });
    }

    private getChosenDates = (): string[] => {
        const valueString = this.state.chosenDatesType === ChosenDateType.Only
            ? this.props.existingParameters?.openOnlyOnDates
            : this.props.existingParameters?.datesToExclude;

        return valueString == null || valueString == "" ? [] : valueString.split(";");
    }

    private updateChosenDates = (dates: string[]) => {
        const cleaned = _.reject(dates, (date) => date === "");
        const sorted = _.sortBy(cleaned);

        const propToChange = this.state.chosenDatesType === ChosenDateType.Only
            ? "openOnlyOnDates" : "datesToExclude";
        if (this.props.onChange) {
            this.props.onChange(sorted.join(";"), propToChange);
        }
        this.setState(state => ({ ...state, selectedChosenDates: sorted }));
    }

    private addSpecificDate = (e: Date | null, event: React.SyntheticEvent<any, Event> | undefined) => {
        const newDate = Time.format(e ?? new Date(), Time.dateFormat);
        const currentValues = this.getChosenDates();

        if (currentValues.some((s) => s === newDate)) {
            Utilities.toastError("Date already exists, choose another.");
            return;
        }

        currentValues.push(newDate);

        this.updateChosenDates(currentValues);
    }

    private clearChosenDates = () => {
        this.updateChosenDates([]);
    }

    private handleRemoveChosenDates = (newDates: Option[]) => {
        const dateValues = newDates.map((x) => x.value);
        this.updateChosenDates(dateValues);
    }

    private processSpecificDatesImport = async (importResult: any) => {
        await Utilities.parseJsonResponse<string[]>(importResult)
            .then((result) => this.updateChosenDates(result))
            .catch((error) => Utilities.toastError(error))
    }

    private toggleSeasonEndDate = (add: boolean) => {
        if (add === false) {
            if (this.props.onChange) {
                this.props.onChange(null, "seasonEndDate");
            }
        } else {
            this.handleChange(new Date(), "seasonEndDate");
        }
        this.setState({ showDetails: this.state.showDetails, showSeasonEndDate: add });
    }

    private getTimePicker(key: TimeType, label: string) {

        const hourAndMinute = key === "startTime" ? this.props.existingParameters?.startTime : this.props.existingParameters?.endTime;

        const parsedDate = Time.parse(hourAndMinute ? hourAndMinute : new Date().toDateString(), "HH:mm");
        const parsedHour = parsedDate.getHours();
        const parsedMinute = parsedDate.getMinutes();

        return (
            <div id={key + "Select"}>
                <S.Label>{label}</S.Label>
                <S.FlexContainer centerItems>
                    <div id={this.props.id + "hour-selector"}>
                        <IntegerDropdown
                            isDisabled={this.props.disabled}
                            isLeftSide
                            maxValue={24}
                            selectedValue={parsedHour}
                            zeroPad={true}
                            onChange={newValue => this.handleStartOrEndTimeUpdate(newValue, key + "Hour")}
                        />
                    </div>
                    <div style={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        borderTop: `2px solid ${S.Colors.grey_2}`,
                        borderBottom: `2px solid ${S.Colors.grey_2}`,
                        color: S.Colors.grey_1,
                        fontSize: '18px',
                        fontWeight: 'bold',
                        paddingBottom: '3px',
                    }}>
                        <span>:</span>
                    </div>
                    <div id={this.props.id + "minute-selector"}>
                        <IntegerDropdown
                            isDisabled={this.props.disabled}
                            isLeftSide={false}
                            maxValue={60}
                            selectedValue={parsedMinute}
                            zeroPad={true}
                            onChange={newValue => this.handleStartOrEndTimeUpdate(newValue, key + "Minute")}
                        />
                    </div>
                </S.FlexContainer>
            </div>
        );
    }

    private generateCheckBoxes(key: keyof (IScheduleParameters), label: string, inputDataModel: IValueLabel[]) {
        return (
            <S.FormGroup id={key}>
                <S.Label>{label}</S.Label>
                {this.getCheckBoxViewModels(key, inputDataModel).map((data) => {
                    return (
                        <S.FlexContainer key={data.value}>
                            <Checkbox
                                disabled={this.props.disabled}
                                key={data.value}
                                name={data.value}
                                onChange={() => this.handleCheckBoxClick(data.value, key, data.selected)}
                                checked={data.selected}
                                value={data.value}
                            />
                            <S.Label>{data.label}</S.Label>
                        </S.FlexContainer>
                    );
                })}
            </S.FormGroup>
        );
    }

    private generateDaysSelectForMonthlyView = (data: IValueLabel[]) => {
        const list: string = _.get(this.props.existingParameters, "cycleDaysString", "None");
        const items = (!list || list === "None") ? [] : list.split(",");
        const values: Array<Option<any>> = data.map((v) => ({
            label: v.label,
            value: v.value
        }));

        const handleChangeChosenDay = (options: Option[]) => {
            if (this.props.onChange) {
                const joinedValues = options.map((x) => x.value).join(",");
                this.props.onChange(joinedValues, "cycleDaysString");
            }
        }

        return (
            <S.FormGroup id="cycleDaysString">
                <S.Label>{FRENDS.Localizations.OnWeekdays}</S.Label>
                <MultiSelect
                    classNamePrefix="react-select"
                    value={Utilities.valuesFromList(values, items)}
                    placeholder={"Select days"}
                    options={values}
                    onChange={handleChangeChosenDay}
                />
            </S.FormGroup>
        )
    }

    private getDetailedMonthlyView() {
        const value = _.get(this.props.existingParameters, "monthlyCycleType");
        switch (value) {
            case "OnDays":
                return (
                    <div>
                        {this.generateDaysSelectForMonthlyView(CycleMonthDays)}
                    </div>);
            case "BasedOnDayOrder":
                return (
                    <div>
                        {this.generateCheckBoxes("cycleDayRanks", FRENDS.Localizations.Ordinal, CycleMonthRanks)}
                        {this.generateCheckBoxes("cycleDaysOfWeek", FRENDS.Localizations.OnWeekdays, ExtendedWeekDays)}
                    </div>);
            default:
                return null;
        }
    }

    private getCycleInputs() {
        const value = _.get(this.props.existingParameters, "cycleType");
        const executedMonthlyType = CycleMonthType.find((f) => f.value === _.get(this.props.existingParameters, "monthlyCycleType"))?.label;
        switch (value) {
            case "Daily":
                return (
                    <div>
                        <TriggerInput existingParameters={this.props.existingParameters} name="cycleRecurEvery" label={FRENDS.Localizations.IsExecutedOn + " " + FRENDS.Localizations.EveryDay} inputMode={InputMode.number} elementId={this.props.id} />
                    </div>);
            case "Weekly":
                return (
                    <div>
                        <TriggerInput existingParameters={this.props.existingParameters} name="cycleRecurEvery" label={FRENDS.Localizations.IsExecutedOn + " " + FRENDS.Localizations.EveryWeek} inputMode={InputMode.number} elementId={this.props.id} />
                        {this.generateCheckBoxes("cycleDaysOfWeek", FRENDS.Localizations.OnWeekdays, WeekDays)}
                    </div>);
            case "Monthly":
                return (
                    <div>
                        {this.generateCheckBoxes("cycleMonths", FRENDS.Localizations.InMonths, CycleMonths)}
                        <DropdownInput dropdownModel={CycleMonthType} elementId={this.props.id} key="monthlyCycleType" identifier="monthlyCycleType" label={`Executed: ${executedMonthlyType}`} />
                        {this.getDetailedMonthlyView()}
                    </div>
                );
            default:
                return null;
        }
    }

    // tslint:disable-next-line:member-ordering
    public render() {

        const chosenDateTypeOptions: Option[] = [
            {
                value: ChosenDateType.Never,
                label: FRENDS.Localizations.Schedule_NeverOnTheseDates
            }, {

                value: ChosenDateType.Only,
                label: FRENDS.Localizations.Schedule_OnlyOnTheseDates
            }
        ];

        const executedCycleType = _.get(this.props.existingParameters, "cycleType");

        return (
            <div>
                <S.FormGroup $inline={true} style={{ justifyContent: "flex-start", gap: "15px" }}>
                    {this.getTimePicker("startTime", FRENDS.Localizations.StartTime)}
                    {this.getTimePicker("endTime", FRENDS.Localizations.EndTime)}
                </S.FormGroup>
                <DropdownInput
                    dropdownModel={this.props.timeZoneData?.timeZones}
                    elementId={this.props.id}
                    key="timeZone"
                    identifier="timeZone"
                    notSelectedLabel={FRENDS.Localizations.ScheduleTimeZoneNotSelected}
                    label={FRENDS.Localizations.TimeZone}
                />
                <TriggerInput
                    existingParameters={this.props.existingParameters}
                    name="recurring"
                    label={"Is repeated"}
                    inputMode={InputMode.boolean}
                    elementId={this.props.id}
                />
                {this.props.existingParameters?.recurring && (
                    <div>
                        <S.Label>every</S.Label>
                        <S.FormGroup $inline={true}>
                            <TriggerInput
                                extraClasses="col-xs-8 no-left-padding"
                                existingParameters={this.props.existingParameters}
                                name="repeatDelay"
                                inputMode={InputMode.number}
                                disabled={this.isRecurringDisabled()}
                                elementId={this.props.id}
                                onUpdated={this.handleRepeatDelayUpdate}
                            />
                            <DropdownInput
                                extraClasses="col-xs-4 no-padding"
                                disabled={this.isRecurringDisabled()}
                                dropdownModel={RepeatDelayType}
                                elementId={this.props.id}
                                key="repeatDelayType"
                                identifier="repeatDelayType"
                                label={null}
                                onUpdated={this.handleRepeatDelayTypeUpdate}
                            />
                        </S.FormGroup>
                    </div>
                )}
                <TriggerInput
                    existingParameters={this.props.existingParameters}
                    name="limitToOneConcurrentExecution"
                    label={FRENDS.Localizations.LimitToOneConcurrentExecution}
                    inputMode={InputMode.boolean}
                    elementId={this.props.id}
                />
                <SwitchCollapse
                    isOpenByDefault={this.state.showDetails}
                    childrenOpen={
                        <div>
                            <S.FormGroup $inline={true}>
                                <div id={"seasonStartDate"}>
                                    <S.Label>{FRENDS.Localizations.SeasonStartDate}</S.Label>
                                    <S.FormGroup $noMargin={true} $inline={true}>
                                        <ReactDatePicker
                                            popperModifiers={popperOverflowModifier}
                                            disabled={this.props.disabled}
                                            className="form-control"
                                            dateFormat={Time.dateFormat}
                                            selected={this.getDate("seasonStartDate")}
                                            onChange={e => this.handleChange(e, "seasonStartDate")}
                                        />
                                        {this.state.showSeasonEndDate || this.props.disabled ? null : <S.Button onClick={() => this.toggleSeasonEndDate(true)}>+ {FRENDS.Localizations.SeasonEndDate}</S.Button>}
                                    </S.FormGroup>
                                </div>
                                {this.state.showSeasonEndDate ?
                                    <div>
                                        <S.Label>{FRENDS.Localizations.SeasonEndDate}</S.Label>
                                        <div>
                                            <ReactDatePicker
                                                popperModifiers={popperOverflowModifier}
                                                disabled={this.props.disabled}
                                                className="form-control"
                                                dateFormat={Time.dateFormat}
                                                selected={this.getDate("seasonEndDate")}
                                                onChange={e => this.handleChange(e, "seasonEndDate")}
                                            />
                                            {!this.props.disabled && (
                                                <S.Button style={{ marginTop: "5px" }} variant={S.ButtonType.bordered} onClick={() => this.toggleSeasonEndDate(false)}>{FRENDS.Localizations.Remove}</S.Button>
                                            )}
                                        </div>
                                    </div>
                                    : null
                                }
                            </S.FormGroup>
                            <DropdownInput dropdownModel={CycleType} elementId={this.props.id} key={"cycleType"} identifier={"cycleType"} label={`Executed: ${executedCycleType}`} />
                            {this.getCycleInputs()}
                            <hr />
                            <div>
                                <S.FormGroup id={"chosenDates"}>
                                    <S.Label>{FRENDS.Localizations.Schedule_ChosenDates}</S.Label>
                                    <Select
                                        classNamePrefix="react-select"
                                        value={Utilities.valueFromId(chosenDateTypeOptions, this.state.chosenDatesType)}
                                        options={chosenDateTypeOptions}
                                        id="chosen-date-type-select"
                                        onChange={this.handleChosenDateTypeChanged}
                                        isDisabled={this.props.disabled}
                                    />
                                </S.FormGroup>
                                <S.FormGroup $inline={true} style={{ gap: "5px" }}>
                                    <MultiSelect
                                        placeholder="No selected dates"
                                        classNamePrefix="react-select"
                                        isClearable={false}
                                        menuIsOpen={false}
                                        hideDropdownIndicator={true}
                                        options={undefined}
                                        isSearchable={false}
                                        value={this.getChosenDates().map((date) => ({
                                            label: date,
                                            value: date
                                        }))}
                                        id="chosen-date-type-select"
                                        onChange={this.handleRemoveChosenDates}
                                        isDisabled={this.props.disabled}
                                    />
                                    <div>
                                        <ReactDatePicker
                                            popperModifiers={popperOverflowModifier}
                                            allowSameDay={false}
                                            disabled={this.props.disabled}
                                            customInput={<S.Button>{FRENDS.Localizations.Add}</S.Button>}
                                            onChange={this.addSpecificDate}
                                        />
                                        <S.Button style={{ marginTop: "5px" }} variant={S.ButtonType.bordered} onClick={this.clearChosenDates} disabled={this.props.disabled}>{FRENDS.Localizations.Clear}</S.Button>
                                    </div>
                                </S.FormGroup>
                                <S.FormGroup>
                                    <S.Button disabled={this.props.disabled}>
                                        <Uploader
                                            buttonText={FRENDS.Localizations.ImportDates}
                                            uploadUrl="api/import/scheduleTrigger/dates"
                                            resultHandler={this.processSpecificDatesImport}
                                            accepts=".ics"
                                            disabled={this.props.disabled}
                                        />
                                    </S.Button>
                                </S.FormGroup>
                            </div>
                        </div>
                    }
                    childrenCollapsed={null}
                />
                <hr />
            </div>
        );
    }

    // TODO: Handle these migrations separately? Perhaps only connect the form instead of individual inputs?
    private handleRepeatDelayUpdate = (newValue: number) => {
        // trigger onChange also for repeatDelaySeconds, as that is needed for the backend
        const repeatDelayInSeconds = this.props.existingParameters !== undefined && this.props.existingParameters.repeatDelayType * newValue;
        if (this.props.onChange) {
            this.props.onChange(repeatDelayInSeconds, "repeatDelaySeconds");
        }
    }

    private handleRepeatDelayTypeUpdate = (newValue: number) => {
        // trigger onChange also for repeatDelaySeconds, as that is needed for the backend
        const repeatDelayInSeconds = this.props.existingParameters !== undefined && this.props.existingParameters.repeatDelay * newValue;
        if (this.props.onChange) {
            this.props.onChange(repeatDelayInSeconds, "repeatDelaySeconds");
        }
    }

    private handleStartOrEndTimeUpdate = (newValue: number, key: string) => {
        // trigger onChange also for startTime and endTime, as that is needed for the backend
        const p = this.props.existingParameters;

        let paddedValue = newValue.toString();
        if (newValue < 10) {
            paddedValue = ("0" + newValue).toString()
        }

        switch (key) {
            case "startTimeHour":
                if (this.props.onChange) {
                    this.props.onChange(paddedValue, "startTimeHour");
                    this.props.onChange(paddedValue + ":" + p?.startTimeMinute, "startTime");
                }
                break;
            case "startTimeMinute":
                if (this.props.onChange) {
                    this.props.onChange(paddedValue, "startTimeMinute");
                    this.props.onChange(p?.startTimeHour + ":" + paddedValue, "startTime");
                }
                break;
            case "endTimeHour":
                if (this.props.onChange) {
                    this.props.onChange(paddedValue, "endTimeHour");
                    this.props.onChange(paddedValue + ":" + p?.endTimeMinute, "endTime");
                }
                break;
            case "endTimeMinute":
                if (this.props.onChange) {
                    this.props.onChange(paddedValue, "endTimeMinute");
                    this.props.onChange(p?.endTimeHour + ":" + paddedValue, "endTime");
                }
                break;
        }
    }
}


interface ScheduleTriggerProps {
    id?: string;
    existingParameters?: IScheduleParameters;
    onChange?: IOnInputChange;
    timeZoneData?: ITimeZoneData | null;
    disabled?: boolean;
}

interface ITimeZoneData {
    currentTimeZone: string;
    timeZones: IValueLabel[];
}

interface ScheduleTriggerState {
    showDetails: boolean;
    showSeasonEndDate: boolean;
    chosenDatesType: ChosenDateType;
    selectedChosenDates: string[];
}

type TimeType = "startTime" | "endTime";

type IOnInputChange = (value: string | number | boolean | null, key: keyof (IScheduleParameters)) => void;

const mapScheduleTriggerStateToProps = (state: State, ownProps: ScheduleTriggerProps): ScheduleTriggerProps => {
    return {
        timeZoneData: getTimeZoneSelectModel(state),
        disabled: state.uiState.disableEdit
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: ScheduleTriggerProps): ScheduleTriggerProps => {
    return {
        onChange: (newValue: string | null, key: string) => dispatch(updateParameter(ownProps.id, key, newValue))
    };
};

export default connect(mapScheduleTriggerStateToProps, mapDispatchToProps)(ScheduleTrigger);
